// discountLineGroups.ts — L232 (Round 6 Wave C, Task 7). PURE — no React, no
// network.
//
// 🔒 WHY. PROP-90384: Karan saw "2500 L tank @42.5%" and nothing else. The two
// heat pumps on the same quote were held too, never shown, never approved, and
// went out anyway. The backend now raises ONE request per held line and
// releases the document only when every line is decided (Task 5). This file
// is the grouping the web card renders that contract with.
//
// 🔒 The header count is `documentDecided` / `documentTotal` from the server,
// NEVER the rows visible in this response: a manager's scope, the status
// filter and the seat toggle all hide rows, and the document has the same
// number of lines regardless of who is looking.

export type DocWord = 'quote' | 'order' | 'invoice' | 'document';

/** Ruling W-C-6 — proposal→quote, sales_order→order, invoice→invoice, else document. */
export function docWordFor(documentType: string | null | undefined): DocWord {
  const t = (documentType ?? '').trim().toLowerCase();
  if (t === 'proposal') return 'quote';
  if (t === 'sales_order') return 'order';
  if (t === 'invoice') return 'invoice';
  return 'document';
}

export interface LineGroupRow {
  id: string;
  proposalId?: string | null;
  salesOrderId?: string | null;
  invoiceId?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  customerName?: string | null;
  /** 2026-09-26 — the dealer behind the document (buyer or on-behalf), when known. */
  dealerName?: string | null;
  status: string;
  // L232 list contract (Task 5) — absent on an older backend.
  lineIndex?: number | null;
  lineCount?: number | null;
  documentDecided?: number | null;
  documentTotal?: number | null;
}

export interface DiscountLineGroup<T extends LineGroupRow> {
  key: string;
  docNumber: string | null;
  customerName: string | null;
  dealerName: string | null;
  docWord: DocWord;
  rows: T[];
  decided: number;
  total: number;
  /** true only when some row says the document holds more than one line. */
  grouped: boolean;
}

function groupKey(r: LineGroupRow): string {
  if (r.proposalId) return `proposal:${r.proposalId}`;
  if (r.salesOrderId) return `sales_order:${r.salesOrderId}`;
  if (r.invoiceId) return `invoice:${r.invoiceId}`;
  // Lead-only / pre-save request: its own single-row document.
  return `request:${r.id}`;
}

/**
 * Group rows by document, first-appearance order, row order preserved.
 * A group is `grouped` only when some row carries `lineCount > 1` — rows from
 * an older backend (no L232 fields) and single-line documents stay ungrouped
 * and render byte-identically to today.
 */
export function groupDiscountRequests<T extends LineGroupRow>(rows: T[]): Array<DiscountLineGroup<T>> {
  const byKey = new Map<string, DiscountLineGroup<T>>();
  for (const r of rows) {
    const key = groupKey(r);
    let g = byKey.get(key);
    if (!g) {
      g = {
        key,
        docNumber: (r.documentNumber ?? '').trim() || null,
        customerName: (r.customerName ?? '').trim() || null,
        dealerName: (r.dealerName ?? '').trim() || null,
        docWord: docWordFor(r.documentType),
        rows: [],
        decided: 0,
        total: 0,
        grouped: false,
      };
      byKey.set(key, g);
    }
    g.rows.push(r);
    if ((r.lineCount ?? 1) > 1) g.grouped = true;
    // Every row on the document carries the same document-level counts; take
    // the largest seen so a stale row cannot understate progress.
    g.decided = Math.max(g.decided, Number(r.documentDecided ?? 0) || 0);
    g.total = Math.max(g.total, Number(r.documentTotal ?? 0) || 0);
  }
  for (const g of byKey.values()) {
    // Older backend (no documentTotal): a document is at least its one row.
    if (g.total < 1) g.total = 1;
    // 🔒 The API answers createdAt DESC, so line 3 arrives first. The card
    // expands line 1 and lists 2, 3 below it (mockup), and Approve-all posts
    // 1 → N. Sort by lineIndex ASC; whole-order rows (null) last; stable.
    g.rows.sort((a, b) => (a.lineIndex ?? Infinity) - (b.lineIndex ?? Infinity));
  }
  return [...byKey.values()];
}

// "39" / "7.5" — drop trailing .0 (same rule as the card's fmtPct).
function fmtPct(p: number): string {
  return Number.isInteger(p) ? String(p) : String(Math.round(p * 10) / 10);
}

/** `Approve this line at 42%` on a multi-line document, else today's `Approve`. */
export function approveLineLabel(pct: number, lineCount: number | null | undefined): string {
  if ((lineCount ?? 1) > 1) return `Approve this line at ${fmtPct(Number(pct))}%`;
  return 'Approve';
}

export const REJECT_LINE_LABEL = 'Reject this line';

function joinNumbers(ns: number[]): string {
  if (ns.length <= 1) return ns.join('');
  return `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`;
}

/**
 * `Approves this line only. The quote stays held until lines 2 and 3 are decided.`
 * Lists the OTHER line numbers; singular for a 2-line document.
 */
export function heldHelperText(docWord: DocWord, lineIndex: number, lineCount: number): string {
  const others: number[] = [];
  for (let i = 1; i <= lineCount; i++) if (i !== lineIndex) others.push(i);
  const clause = others.length === 1
    ? `line ${others[0]} is approved`
    : `lines ${joinNumbers(others)} are approved`;
  return `Approves this line only. The ${docWord} stays held until ${clause}.`;
}

/**
 * `Quote held · 1 of 3 lines answered` — the header pill while lines remain.
 * Once every line has an answer: `Quote · all 3 lines answered` — NEUTRAL.
 *
 * 🔒 WC-06: counts cannot prove a release. A REJECTED line is answered but
 * keeps the document held (the rep must re-quote it), so `decided >= total`
 * never earns the word "released" here. A release is announced only from the
 * server's `documentReleased`.
 *
 * 🔒 M3 (round review, 2026-09-23) — WHY "answered", NOT "decided". The footer
 * (`heldHelperText`) promises release "until line 2 is approved", so on a
 * rejected line Karan read "all 2 lines decided" above a card with no buttons
 * and a footer talking about approval: two vocabularies for two different things
 * on one card, one of which sounded like a release. The count is deliberately
 * NOT the release word now — "answered" states what it measures (every line has
 * a verdict) and cannot be mistaken for "approved".
 */
export function heldHeaderText(docWord: DocWord, decided: number, total: number): string {
  const Word = docWord.charAt(0).toUpperCase() + docWord.slice(1);
  if (decided >= total) return `${Word} · all ${total} lines answered`;
  return `${Word} held · ${decided} of ${total} lines answered`;
}

/** `PROP-90390 released` / `Quote released` — the server said so, nothing else may. */
export function releasedNoticeText(docNumber: string | null | undefined, docWord: DocWord): string {
  const n = (docNumber ?? '').trim();
  if (n) return `${n} released`;
  const Word = docWord.charAt(0).toUpperCase() + docWord.slice(1);
  return `${Word} released`;
}

/**
 * D2 (2026-09-23) — is the DOCUMENT released, i.e. may the card offer
 * `Convert SO` / `Download proposal` without guaranteeing a 423?
 *
 * 🔒 The release rule belongs to the SERVER — `clearDocumentHold`
 * (gate-and-hold.ts:279-293): the hold clears on ZERO pending AND ZERO rejected,
 * over ALL the document's requests. A rejected line is "decided" but blocks
 * unconditionally, so `documentDecided === documentTotal` is NOT a release and
 * is never read here (same rule as heldHeaderText's WC-06 note above).
 *
 * PROP-90543 (Manak, 2026-09-23): line 1 approved, line 2 pending. The row's
 * own `approved` status said yes, the document said no, and both buttons were
 * dead taps — Download opened a tab of raw JSON 423.
 *
 * 🔒 I1 (round review, 2026-09-23) — WHY THE SERVER'S FLAG WINS. The first fix
 * INFERRED the release from `siblings` + `documentTotal`, and that inference is
 * strictly STRICTER than the rule it claimed to mirror: `siblings` carries only
 * PRODUCT-scope rows while `documentTotal` counts every request, so a quote
 * holding a document-scope request — or a `superseded` one — could never satisfy
 * `siblings.length + 1 >= documentTotal`. The strip then vanished PERMANENTLY
 * from a fully approved, fully released quote: Monika approves every line and
 * still cannot download the proposal. `documentReleased` is the server's own
 * verdict over rows this caller may not even be allowed to see, so it wins.
 *
 * Fail-soft, unchanged in direction: an older backend sends no `documentReleased`
 * at all, and the old inference applies — a lone approved request on a payload
 * with no L232 fields is released (byte-identical to today's single-line card),
 * and a document known to hold more lines than this response can show is
 * refused. An unproven release is a missing button, never a dead tap.
 */
export function documentReleasedForRow(row: {
  status: string;
  siblings?: Array<{ status: string }> | null;
  lineCount?: number | null;
  documentTotal?: number | null;
  documentReleased?: boolean | null;
}): boolean {
  if (row.status !== 'approved') return false;
  if (typeof row.documentReleased === 'boolean') return row.documentReleased;
  const siblings = Array.isArray(row.siblings) ? row.siblings : [];
  if (siblings.some((s) => String(s?.status ?? '').trim().toLowerCase() !== 'approved')) return false;
  const total = row.documentTotal ?? row.lineCount ?? 1;
  return siblings.length + 1 >= total;
}
